import { useEffect, useRef, useState } from "react";
import { Play, Pause, MusicNotes, ArrowsClockwise, Trash, SpeakerHigh, SpeakerSimpleX } from "@phosphor-icons/react";
import { fileUrl } from "../../lib/api";
import { Slider } from "../ui/slider";

export default function AudioTrackBar({ audio, onChange, onRemove }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  const src = audio && audio.storage_path ? fileUrl(audio.storage_path) : null;
  const loop = audio?.loop !== false;
  const volume = audio?.volume ?? 1;

  useEffect(() => {
    if (ref.current) {
      ref.current.volume = volume;
      ref.current.loop = loop;
    }
  }, [volume, loop, src]);

  useEffect(() => { setPlaying(false); }, [src]);

  if (!src) return null;

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => {}); }
    else { a.pause(); setPlaying(false); }
  };

  return (
    <div
      data-testid="audio-track-bar"
      className="absolute bottom-4 left-4 z-30 flex items-center gap-2 rounded-full bg-[#161616]/95 backdrop-blur border border-white/10 shadow-xl pl-2 pr-3 py-2"
    >
      <audio ref={ref} src={src} onEnded={() => setPlaying(false)} crossOrigin="anonymous" />
      <button onClick={toggle} data-testid="audio-play-btn" className="h-8 w-8 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center text-white transition-colors">
        {playing ? <Pause weight="fill" size={15} /> : <Play weight="fill" size={15} />}
      </button>
      <MusicNotes weight="fill" size={15} className="text-blue-400" />
      <span className="text-xs text-zinc-200 max-w-[120px] truncate">{audio.filename || "Background audio"}</span>
      <button
        onClick={() => onChange({ loop: !loop })}
        data-testid="audio-loop-btn"
        title="Loop"
        className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${loop ? "bg-blue-600/30 text-blue-300" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}
      >
        <ArrowsClockwise size={14} weight="bold" />
      </button>
      <div className="flex items-center gap-1.5 w-24">
        {volume === 0 ? <SpeakerSimpleX size={15} className="text-zinc-400" /> : <SpeakerHigh size={15} className="text-zinc-400" />}
        <Slider value={[Math.round(volume * 100)]} min={0} max={100} step={1} onValueChange={(v) => onChange({ volume: v[0] / 100 })} />
      </div>
      <button onClick={onRemove} data-testid="audio-bar-remove" className="h-7 w-7 rounded-full bg-white/5 hover:bg-red-600/20 text-zinc-400 hover:text-red-400 flex items-center justify-center transition-colors">
        <Trash size={14} />
      </button>
    </div>
  );
}
